import React, { useCallback, useEffect, useRef, useState } from "react";
import { Camera, FileText, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { fileToBase64 } from "@/lib/utils-app";

const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT = "application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif";

export function responsivaIsPdf(value) {
  return typeof value === "string" && value.startsWith("data:application/pdf");
}

export function responsivaPreviewSrc(value, thumb) {
  if (responsivaIsPdf(value)) return null;
  if (thumb && !responsivaIsPdf(thumb)) return thumb;
  if (value && !responsivaIsPdf(value)) return value;
  return null;
}

export function openResponsivaDocument(value) {
  if (!value) return;
  const w = window.open(value, "_blank", "noopener,noreferrer");
  if (!w) toast.error("Permite ventanas emergentes para ver el documento");
}

export default function ResponsivaDocumentField({
  value,
  onChange,
  label = "Carta responsiva firmada (opcional)",
  hint = "Opcional. Documento firmado por el suscriptor con el acuerdo entre lo contratado y lo que espera del servicio. PDF o foto escaneada.",
}) {
  const [hasCamera, setHasCamera] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [stream, setStream] = useState(null);
  const [fileName, setFileName] = useState("");
  const streamRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    navigator.mediaDevices
      .enumerateDevices()
      .then((devices) => setHasCamera(devices.some((d) => d.kind === "videoinput")))
      .catch(() => setHasCamera(false));
  }, []);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks?.().forEach((t) => t.stop());
    streamRef.current = null;
    setStream(null);
  }, []);

  useEffect(() => () => stopStream(), [stopStream]);

  const openCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Este navegador no permite usar la cámara");
      return;
    }
    try {
      const media = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      streamRef.current = media;
      setStream(media);
      setCameraOpen(true);
    } catch {
      toast.error("No se pudo acceder a la cámara");
    }
  };

  useEffect(() => {
    if (cameraOpen && videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [cameraOpen, stream]);

  const closeCamera = () => {
    stopStream();
    setCameraOpen(false);
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) {
      toast.error("Espera a que la cámara esté lista");
      return;
    }
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, w, h);
    onChange(canvas.toDataURL("image/jpeg", 0.92));
    setFileName("carta-fotografiada.jpg");
    toast.success("Carta capturada");
    closeCamera();
  };

  const pickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const okType =
      file.type === "application/pdf" || file.type.startsWith("image/");
    if (!okType) {
      toast.error("Solo PDF o imagen (JPG, PNG, WebP)");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("El archivo no debe superar 10 MB");
      return;
    }
    try {
      onChange(await fileToBase64(file));
      setFileName(file.name);
      toast.success(file.type === "application/pdf" ? "PDF cargado" : "Imagen cargada");
    } catch {
      toast.error("No se pudo leer el archivo");
    }
  };

  const clear = () => {
    onChange(null);
    setFileName("");
  };

  const isPdf = responsivaIsPdf(value);
  const preview = responsivaPreviewSrc(value);

  return (
    <>
      <div>
        {label && (
          <label className="text-xs uppercase tracking-widest text-white/60">{label}</label>
        )}
        <div className={`flex items-start gap-4 flex-wrap ${label ? "mt-2" : ""}`}>
          <div className="w-28 h-36 rounded-sm overflow-hidden bg-white/5 border border-white/10 grid place-items-center shrink-0">
            {isPdf ? (
              <div className="text-center p-2">
                <FileText className="text-[#E63946] mx-auto" size={36} />
                <p className="text-[10px] text-white/60 mt-2 uppercase tracking-wide">PDF</p>
              </div>
            ) : preview ? (
              <img src={preview} alt="" className="w-full h-full object-contain bg-black/40" />
            ) : (
              <FileText className="text-white/25" size={32} />
            )}
          </div>
          <div className="flex flex-col gap-2 min-w-[200px]">
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="btn-outline text-xs py-2"
              >
                <Upload size={12} /> Subir PDF o imagen
              </button>
              {hasCamera && (
                <button type="button" onClick={openCamera} className="btn-outline text-xs py-2">
                  <Camera size={12} /> Fotografiar carta
                </button>
              )}
              {value && (
                <>
                  <button
                    type="button"
                    onClick={() => openResponsivaDocument(value)}
                    className="btn-outline text-xs py-2"
                  >
                    Ver documento
                  </button>
                  <button
                    type="button"
                    onClick={clear}
                    className="btn-outline text-xs py-2 text-red-400 border-red-400/30"
                  >
                    Quitar
                  </button>
                </>
              )}
            </div>
            {fileName && value && (
              <p className="text-[10px] text-white/40 truncate max-w-xs">{fileName}</p>
            )}
            <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" onChange={pickFile} />
          </div>
        </div>
        {hint && <p className="text-[10px] text-white/40 mt-2 max-w-xl">{hint}</p>}
      </div>

      {cameraOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4"
          onClick={closeCamera}
        >
          <div
            className="glass-strong max-w-lg w-full rounded-sm p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="font-display text-xl text-white">Fotografiar carta firmada</span>
              <button type="button" onClick={closeCamera} className="text-white/60 hover:text-white">
                <X size={20} />
              </button>
            </div>
            <div className="relative aspect-[3/4] bg-black rounded-sm overflow-hidden">
              <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
            </div>
            <canvas ref={canvasRef} className="hidden" />
            <p className="text-xs text-white/40 mt-2">
              Encuadra la carta responsiva firmada con buena luz y texto legible.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={closeCamera} className="btn-outline text-sm py-2">
                Cancelar
              </button>
              <button type="button" onClick={capturePhoto} className="btn-primary text-sm py-2">
                <Camera size={14} /> Capturar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
